/* eslint-disable @typescript-eslint/ban-ts-ignore */
import { VueConstructor } from "vue";
import Router, { RawLocation, Route } from "vue-router";
import { Store } from "vuex";
import { ACTION_TYPES, MUTATION_TYPES } from "@/store/const";
import { StateRoot } from "@/store/state";
import { getLoginData } from "@/lib/authHelper";
import emitter from "@/lib/emitter";
import HistoryManager from "@/lib/historyManager";
import {
  allowAccess as allowAccessHelper,
  archivedUser as archivedUserHelper,
  checkPendingCreator,
  checkUnverifiedCreator,
} from "@/lib/loginHelpers";
import { setReferral } from "@/lib/referralHelpers";
import { getProfileUrl } from "@/lib/urlHelpers";
import { EVENTS } from "@/const";

const isLoginRoute = (route: Route) => !!route.meta?.login || route.name === "logout" || route.path === "/";

function buildAuthContext(store: Store<StateRoot>) {
  const user = store.state.user;

  const allowAccess = allowAccessHelper(user);
  const isArchivedUser = archivedUserHelper(user);
  const isUnverifiedCreator = checkUnverifiedCreator(user, store.state.verified);
  const isPendingCreator = checkPendingCreator(user, store.state.verified);

  return {
    user,
    loggedIn: !!user,
    allowAccess,
    isArchivedUser,
    isUnverifiedCreator,
    isPendingCreator,
    socialLogin: store.state.socialLogin,
    redirectPath: store.state.redirectPath,
  };
}

function resolveRedirect(to: Route, from: Route, ctx: ReturnType<typeof buildAuthContext>): RawLocation | false | void {
  const {
    loggedIn,
    allowAccess,
    isArchivedUser,
    isUnverifiedCreator,
    isPendingCreator,
    socialLogin,
    redirectPath,
  } = ctx;

  if (to.name === "loginToken") return;
  if (to.matched.some(r => r.meta.private && !loggedIn)) {
    if (["profile", "profileById", "followersById", "followingById"].includes(to.name ?? "")) {
      return { name: "signup" };
    }
    return { name: "index" };
  }
  if (to.name === "logout") {
    return { name: "index" };
  }
  if (isLoginRoute(to) && loggedIn && allowAccess) {
    return { name: "home" };
  }
  if (to.name !== "signupAccountRestore" && loggedIn && !allowAccess && isArchivedUser) {
    return { name: "signupAccountRestore" };
  }
  if (to.name !== "signupEmailConfirm" && loggedIn && !allowAccess && socialLogin && !isArchivedUser) {
    return { name: "signupEmailConfirm" };
  }
  if (to.name !== "signupConfirm" && loggedIn && !allowAccess && !socialLogin && !isArchivedUser) {
    return { name: "signupConfirm" };
  }
  if (to.name !== "becomeCreatorJoin" && loggedIn && allowAccess && isUnverifiedCreator) {
    return { name: "becomeCreatorJoin" };
  }
  if (to.name !== "profilePayeeInfo" && loggedIn && allowAccess && isPendingCreator) {
    return { name: "profilePayeeInfo" };
  }
  if (
    isLoginRoute(from) &&
    !isLoginRoute(to) &&
    loggedIn &&
    allowAccess &&
    redirectPath &&
    !isUnverifiedCreator &&
    !isPendingCreator
  ) {
    return redirectPath;
  }
}

/**
 * Query side effects (referral, redirectPath)
 */
function handleQuerySideEffects(to: Route, store: Store<StateRoot>) {
  if (to.query.redirectPath) {
    store.commit(MUTATION_TYPES.SET_REDIRECT_PATH, to.query.redirectPath);
  }

  if (to.query.referral) {
    const referral = to.query.referral;

    const referralId = Array.isArray(referral)
      ? referral.find<string>((i): i is string => typeof i === "string") || ""
      : referral;

    setReferral(referralId);
    store.commit(MUTATION_TYPES.SET_REDIRECT_PATH, getProfileUrl(referralId));
  }
}

export default {
  install(Vue: VueConstructor, { router, store }: { router: Router; store: Store<StateRoot> }) {
    const origReplace = router.replace;

    // @ts-ignore
    router.replace = function(...args) {
      HistoryManager.setReplace();
      // @ts-ignore
      return origReplace.call(this, ...args);
    };

    router.beforeEach(async (to, from, next) => {
      handleQuerySideEffects(to, store);

      // silent login
      if (!store.state.user && getLoginData()) {
        await store.dispatch(ACTION_TYPES.LOGIN, getLoginData());
      }
      // logout side effect
      if (to.name === "logout") {
        emitter.emit(EVENTS.AUDIO_PLAYER_RESET);
        await store.dispatch(ACTION_TYPES.LOGOUT);
      } else {
        if (store.state.loginProcessing) await store.state.loginProcessing;
      }

      const ctx = buildAuthContext(store);

      const location = resolveRedirect(to, from, ctx);

      if (!isLoginRoute(to) && location && !ctx.redirectPath) {
        store.commit(MUTATION_TYPES.SET_REDIRECT_PATH, to);
      } else if (location === ctx.redirectPath) {
        store.commit(MUTATION_TYPES.SET_REDIRECT_PATH, null);
      }

      next(location);
    });
    router.afterEach(HistoryManager.afterEachGuard.bind(HistoryManager));
  },
};
